import JSZip from 'jszip';
import { Booklet, PosterType } from '../../../types/generated.types';
import { sanitizeFileSystemName } from './bookletFolderUtils';

export enum MediaType {
  SOCIAL_MEDIA = 'socialMedia',
  POSTER = 'poster',
  CHECKOUT_SIGN = 'checkoutSign',
  ROLLUP = 'rollup',
  SCHOOL_CLASS_POSTER = 'schoolClassPoster',
  SCHOOL_CLASS_SOCIAL_MEDIA = 'schoolClassSocialMedia',
  MOBILE_IMAGES = 'mobileImages',
  ASSOCIATION_POSTER = 'associationPoster'
}

interface MediaGenerationResult {
  bookletId: string;
  bookletTitle: string;
  files: string[];
}

interface MediaTypeConfig {
  fileExtension: string;
  mimeType: string;
  getFileName: (bookletTitle: string, index: number, posterType?: PosterType) => string;
}

const mediaTypeConfigs: Record<MediaType, MediaTypeConfig> = {
  [MediaType.SOCIAL_MEDIA]: {
    fileExtension: 'png',
    mimeType: 'image/png',
    getFileName: (bookletTitle, index) => `${sanitizeFileSystemName(bookletTitle)}-social-media-${index + 1}.png`
  },
  [MediaType.POSTER]: {
    fileExtension: 'pdf',
    mimeType: 'application/pdf',
    getFileName: (bookletTitle) => `${sanitizeFileSystemName(bookletTitle)}-affisch.pdf`
  },
  [MediaType.CHECKOUT_SIGN]: {
    fileExtension: 'pdf',
    mimeType: 'application/pdf',
    getFileName: (bookletTitle) => `${sanitizeFileSystemName(bookletTitle)}-vippis.pdf`
  },
  [MediaType.ROLLUP]: {
    fileExtension: 'pdf',
    mimeType: 'application/pdf',
    getFileName: (bookletTitle) => `${sanitizeFileSystemName(bookletTitle)}-rollup.pdf`
  },
  [MediaType.SCHOOL_CLASS_POSTER]: {
    fileExtension: 'pdf',
    mimeType: 'application/pdf',
    getFileName: (bookletTitle, _index, posterType) => {
      const sanitizedTitle = sanitizeFileSystemName(bookletTitle);
      switch (posterType) {
        case PosterType.ClassTrip:
          return `${sanitizedTitle}-klass.pdf`;
        case PosterType.Team:
          return `${sanitizedTitle}-lag.pdf`;
        case PosterType.Graduation:
          return `${sanitizedTitle}-student.pdf`;
        default:
          return `${sanitizedTitle}-skolklass.pdf`;
      }
    }
  },
  [MediaType.SCHOOL_CLASS_SOCIAL_MEDIA]: {
    fileExtension: 'png',
    mimeType: 'image/png',
    getFileName: (bookletTitle, index) => {
      const imageTypes = ['square', 'portrait', 'landscape', 'web'];
      return `${sanitizeFileSystemName(bookletTitle)}-social-${imageTypes[index] || `image-${index + 1}`}.png`;
    }
  },
  [MediaType.MOBILE_IMAGES]: {
    fileExtension: 'png',
    mimeType: 'image/png',
    getFileName: (bookletTitle, index) => {
      const imageTypes = ['booklet', 'single-booklet'];
      return `${sanitizeFileSystemName(bookletTitle)}-${imageTypes[index]}.png`;
    }
  },
  [MediaType.ASSOCIATION_POSTER]: {
    fileExtension: 'pdf',
    mimeType: 'application/pdf',
    getFileName: (bookletTitle, index) => `${sanitizeFileSystemName(bookletTitle)}-föreningsaffisch-${index + 1}.pdf`
  }
};

export const generateSalesMaterialUtil = async (
  booklets: Booklet[],
  associationId: string,
  type: MediaType,
  generateMediaContent: any,
  posterType?: PosterType,
  teamId?: string
): Promise<{ results: MediaGenerationResult[], zip: JSZip }> => {
  const zip = new JSZip();
  const config = mediaTypeConfigs[type];

  const generationPromises = booklets.map(async (booklet) => {
    const response = await generateMediaContent({
      variables: {
        bookletId: booklet._id,
        associationId: associationId,
        type,
        ...(posterType && { posterType }),
        ...(teamId && { teamId })
      }
    });

    const files = response.data?.generateSalesMaterial || [];
    files.forEach((file: string, index: number) => {
      // Convert base64 to blob
      const byteString = atob(file);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }

      const blob = new Blob([ab], { type: config.mimeType });
      const fileName = config.getFileName(booklet.title, index, posterType);
      zip.file(fileName, blob);
    });

    return {
      bookletId: booklet._id,
      bookletTitle: booklet.title,
      files
    };
  });

  const results = await Promise.all(generationPromises);
  return { results, zip };
};

export const downloadZipFile = async (zip: JSZip, fileName: string) => {
  const content = await zip.generateAsync({ type: 'blob' });
  const zipUrl = URL.createObjectURL(content);
  const link = document.createElement('a');
  link.href = zipUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(zipUrl);
}; 
